import { useEffect, useState } from "react";
import {
  Button,
  Chip,
  Group,
  Select,
  Stack,
  Textarea,
  Alert,
  Accordion,
  Loader,
  ChipGroup,
  SegmentedControl,
  Text,
} from "@mantine/core";
import { generateVideo, getGenerationOptions, getVideoModels } from "../api";
import { IconCoins, IconInfoCircle } from "@tabler/icons-react";
import mixpanel from "../tracking";
import { notifications } from "@mantine/notifications";
import { useStore } from "../store";
import { useForm } from "@mantine/form";
import V2PluginRunForm from "./V2PluginRunForm.jsx";
import { usePluginPicker } from "./usePluginPicker";
import { imageThumbUri } from "./imageUris";

export default function GeneratorOptionsVideo() {
  const jobs = useStore((state) => state.jobs);
  const setJobs = useStore((state) => state.setJobs);
  const [selectedLength, setSelectedLength] = useState("5");
  const [videoModels, setVideoModels] = useState([]);
  const [selectedModel, setSelectedModel] = useState(null);
  const [referenceMode, setReferenceMode] = useState("same_person");
  const [resolution, setResolution] = useState("768p");
  const selectedImages = useStore((state) => state.selectedImages);
  const images = useStore((state) => state.galleryImages);
  const [options, setOptions] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Video-domain plugins only, matching the current selection: image-input
  // (I2V) plugins with a selection, text-only (T2V) plugins without.
  const {
    optionSource,
    setOptionSource,
    compatiblePlugins,
    clearedLabel,
  } = usePluginPicker("pv-last-plugin-video", true);

  const pluginSelector = (
    <>
      <Select
        label="Plugin"
        placeholder={
          compatiblePlugins.length === 0
            ? selectedImages.length > 0
              ? "No video plugins for editing images"
              : "No text-to-video plugins"
            : undefined
        }
        disabled={compatiblePlugins.length === 0}
        onChange={setOptionSource}
        value={optionSource}
        clearable
        data={compatiblePlugins.map((p) => ({
          label: p.pluginTitle,
          value: p.id,
        }))}
      />
      {clearedLabel && (
        <Text c="dim" fz="xs">
          {`"${clearedLabel}" doesn't apply to the current selection.`}
        </Text>
      )}
    </>
  );

  useEffect(() => {
    getGenerationOptions(optionSource, "video").then((data) => {
      setOptions(data);
      let conf = {};
      for (let element of data.options) {
        conf[element.title] = [];
      }
    });
  }, [optionSource]);

  // The engine stays a server-driven default (wan-2.5 when registered). Users
  // never pick the model. FAL I2V engines require a gallery selection (the
  // starting frame); Create is disabled without one. Only clip length / quality
  // / aspect are exposed.
  useEffect(() => {
    getVideoModels()
      .then((models) => {
        setVideoModels(models);
        if (!selectedModel && models.length > 0) {
          const preferred =
            models.find((m) => m.baseModel === "wan-2.5") ?? models[0];
          setSelectedModel(preferred.baseModel);
          setSelectedLength(String(preferred.allowedSeconds[0]));
        }
      })
      .catch(() => setVideoModels([]));
  }, [selectedModel]);

  const activeModel = videoModels.find((m) => m.baseModel === selectedModel);
  const allowedLengths = activeModel?.allowedSeconds ?? [5, 10];
  const creditRate =
    (activeModel?.resolutionCredits ?? {})[resolution] ??
    activeModel?.creditsPerSecond ??
    70;
  const creditPreview = activeModel ? creditRate * Number(selectedLength) : 700;

  // Reference-to-video (RunComfy H3): gallery images are identity/context
  // anchors conditioned via the prompt, not a starting frame. The chosen mode
  // decides how strongly the model is told to follow them.
  const isReferenceVideo = !!activeModel?.referenceVideo;
  const maxReferenceImages = activeModel?.maxReferenceImages ?? 1;
  const needsReference = isReferenceVideo && activeModel?.requiresReferenceImage;
  // FAL I2V (wan-2.5, ltx, h3, …): the selected image is the first frame.
  // Default true so a missing catalog flag still cannot submit text-only.
  const needsStartingFrame =
    !isReferenceVideo && activeModel?.requiresInputImage !== false;

  const submitRefImages = isReferenceVideo
    ? selectedImages.slice(0, maxReferenceImages)
    : [];
  const hasEnoughRefs = isReferenceVideo
    ? submitRefImages.length > 0
    : true;

  const nearEqual = (a, b) => {
    return Math.abs(a - b) < 0.075;
  };

  const form = useForm({
    mode: "uncontrolled",
    initialValues: {
      aspectRatio: "9:16",
      prompt: "",
      config: {},
    },
  });

  const handleChipClick = (option) => () => {
    const keyName = option.title;
    form.setFieldValue("config." + keyName, null);
  };

  let selectedImageDetails = null;
  if (selectedImages.length > 0) {
    // console.log("images.data", images?.data);
    selectedImageDetails = images?.data?.find(
      (img) => img.logicalImageId === selectedImages[0]
    );
  }

  // console.debug("selectedImageDetails", selectedImageDetails);
  let selectedImageAspectRatio = null;
  if (selectedImageDetails) {
    selectedImageAspectRatio =
      selectedImageDetails?.pixelImages[0]?.width /
      selectedImageDetails?.pixelImages[0]?.height;
  }

  // console.debug("selectedImageAspectRatio", selectedImageAspectRatio);

  // Recipe-driven (manifest v2) video plugins run through the shared recipe
  // form — its Image inputs come from the gallery selection, which is exactly
  // the I2V flow.
  if (options?.isV2) {
    return (
      <Stack p="xs" gap="xs">
        {pluginSelector}
        <V2PluginRunForm pluginId={optionSource} options={options} />
      </Stack>
    );
  }

  return (
    <form
      onSubmit={form.onSubmit((values) => {
        setIsSubmitting(true);
        var dtoConfig = {};
        for (var x in values.config) {
          const val = values.config[x];
          // check if val is an array:
          if (Array.isArray(val)) dtoConfig[x] = val;
          else dtoConfig[x] = [val];
        }
        const dto = {
          sourceId: optionSource,
          prompt: values.prompt,
          aspectRatio: values.aspectRatio,
          duration: Number(selectedLength),
          baseModel: selectedModel,
          inputImageId:
            !isReferenceVideo && selectedImages.length ? selectedImages[0] : null,
          refImageIds: isReferenceVideo ? submitRefImages : undefined,
          referenceMode: isReferenceVideo ? referenceMode : undefined,
          resolution: (activeModel?.resolutions?.length ?? 0) > 1 ? resolution : undefined,
          config: dtoConfig,
        };
        generateVideo(dto)
          .then((data) => {
            let newJob = {
              // Show the source/reference image behind the processing card.
              uri: imageThumbUri(images, dto.inputImageId ?? dto.refImageIds?.[0]),
              clientId: Math.random().toString(36).substr(2, 9),
              ...data,
            };
            setJobs([newJob, ...jobs]);
            try {
              mixpanel.track("Video gen started");
            } catch {}
          })
          .catch((error) => {
            notifications.show({
              title: "An error occurred!",
              message: error.message,
              color: "red",
            });
          })
          .finally(() => {
            setIsSubmitting(false);
          });
      })}
    >
      <Stack p="xs" gap={4}>
        {pluginSelector}

        <Textarea
          required
          label="Prompt"
          {...form.getInputProps("prompt")}
          minRows={3}
          maxRows={6}
          autosize
        />

        {isReferenceVideo && (
          <Stack gap={6} mt="xs">
            <Text fz="sm">
              Reference mode — how closely to follow your selected image
              {maxReferenceImages > 1 ? "s" : ""}
            </Text>
            <SegmentedControl
              size="xs"
              value={referenceMode}
              onChange={setReferenceMode}
              data={[
                { label: "Same scene", value: "same_scene" },
                { label: "Same person", value: "same_person" },
                { label: "Loose", value: "loose" },
                ...(needsReference
                  ? []
                  : [{ label: "From scratch", value: "scratch" }]),
              ]}
            />
            <Text c="dimmed" fz="xs">
              The model uses your images as identity/context anchors — it does
              not start from them. {"\u201CSame scene\u201D"} keeps everything,{" "}
              {"\u201CLoose\u201D"} uses only mood and style.
            </Text>
          </Stack>
        )}

        {(activeModel?.aspectRatios?.length ?? 0) > 0 && (
          <>
            <Text fz="sm" mt="xs">
              Aspect ratio
            </Text>
            <ChipGroup size="xs" {...form.getInputProps(`aspectRatio`)}>
              <Group gap="xs">
                {(activeModel?.aspectRatios ?? []).map((ar) => {
                  const labels = {
                    "21:9": "21:9 (Ultrawide)",
                    "16:9": "16:9 (Landscape)",
                    "4:3": "4:3 (Classic)",
                    "1:1": "1:1 (Square)",
                    "3:4": "3:4 (Portrait)",
                    "9:16": "9:16 (Portrait)",
                  };
                  return (
                    <Chip variant="light" size="xs" value={ar} key={ar}>
                      {labels[ar] ?? ar}
                    </Chip>
                  );
                })}
              </Group>
            </ChipGroup>
          </>
        )}

        <Group grow>
          <Select
            mt="xs"
            label="Length"
            onChange={setSelectedLength}
            value={selectedLength}
            data={allowedLengths.map((s) => ({
              label: `${s}s`,
              value: String(s),
            }))}
            allowDeselect={false}
          />
          {(activeModel?.resolutions?.length ?? 0) > 1 && (
            <Select
              mt="xs"
              label="Quality"
              onChange={setResolution}
              value={resolution}
              data={(activeModel?.resolutions ?? []).map((r) => ({
                label: r === "2k" ? "2K (best)" : "768p (standard)",
                value: r,
              }))}
              allowDeselect={false}
            />
          )}
        </Group>

        {isReferenceVideo && selectedImages.length === 0 && (
          <Alert
            mt="xl"
            color="yellow"
            title="No reference image selected"
            variant="light"
            p="xs"
            icon={<IconInfoCircle />}
          >
            {needsReference
              ? `Select ${maxReferenceImages > 1 ? `up to ${maxReferenceImages} ` : "an "}image in the gallery to use as identity anchor.`
              : "Without a reference image the video is generated from the prompt only."}
          </Alert>
        )}

        {isReferenceVideo && selectedImages.length > maxReferenceImages && (
          <Alert
            mt="xl"
            color="red"
            title="Too many reference images"
            variant="light"
            p="xs"
            icon={<IconInfoCircle />}
          >
            This model uses at most {maxReferenceImages} reference images. The
            first {maxReferenceImages} selected {maxReferenceImages === 1 ? "is" : "are"} used.
          </Alert>
        )}

        {needsStartingFrame && selectedImages.length === 0 && (
          <Alert
            mt="xl"
            color="yellow"
            title="Source image required"
            variant="light"
            p="xs"
            icon={<IconInfoCircle />}
          >
            Select one image in the gallery. This model animates that image as
            the first frame.
          </Alert>
        )}

        {!isReferenceVideo && selectedImages.length > 1 && (
          <Alert
            mt="xl"
            color="red"
            title="Multiple images selected"
            variant="light"
            p="xs"
            icon={<IconInfoCircle />}
          >
            You can only create one video from a single image at a time
          </Alert>
        )}

        {!isReferenceVideo &&
          selectedImages.length === 1 &&
          nearEqual(selectedImageAspectRatio, 9.0 / 16.0) == false && (
            <Alert
              mt="xl"
              color="red"
              title="Aspect ratio mismatch"
              variant="light"
              p="xs"
              icon={<IconInfoCircle />}
            >
              The selected image has an aspect ratio of{" "}
              {selectedImageAspectRatio?.toFixed(2)}. The video will be
              generated with a 9:16 (portrait) aspect ratio. This may result in
              cropping or stretching of the image. We recommend using a 9:16
              image for best results.
            </Alert>
          )}

        <Accordion>
          {!options && <Loader />}

          {selectedImages.length === 0 &&
            options &&
            options.options &&
            options.options
              .sort(function (b, a) {
                var x = a.title.toLowerCase();
                var y = b.title.toLowerCase();
                return x < y ? -1 : x > y ? 1 : 0;
              })
              .map((option) => (
                <Accordion.Item key={option.title} value={option.title}>
                  <Accordion.Control>{option.title}</Accordion.Control>
                  <Accordion.Panel>
                    {/* {option.values.length > 10 && (
                      <TextInput
                        size="xs"
                        mb="xs"
                        value={filters[option.title]}
                        onChange={(evt) =>
                          setFilters({
                            ...filters,
                            [option.title]: evt.target.value,
                          })
                        }
                      />
                    )} */}
                    <Chip.Group
                      multiple={option.allowMulti}
                      {...form.getInputProps(`config.${option.title}`)}
                    >
                      <Group gap="xs">
                        {option.values
                          // .filter((value) => {
                          //   if (!filters[option.title]) return true;
                          //   return value.titleUser
                          //     .toLowerCase()
                          //     .includes(filters[option.title].toLowerCase());
                          // })
                          .sort(function (a, b) {
                            var x = a.titleUser.toLowerCase();
                            var y = b.titleUser.toLowerCase();
                            return x < y ? -1 : x > y ? 1 : 0;
                          })
                          .map((value) => (
                            // <Tooltip
                            //   label={value.tooltip}
                            //   key={`${option.title}-${value.titleUser}`}
                            // >
                            <Chip
                              key={`${option.title}-${value.titleUser}`}
                              variant="light"
                              onClick={handleChipClick(option)}
                              size="xs"
                              value={value.titleUser}
                            >
                              {value.titleUser}
                            </Chip>
                            // </Tooltip>
                          ))}
                      </Group>
                    </Chip.Group>
                  </Accordion.Panel>
                </Accordion.Item>
              ))}
        </Accordion>

        <Button
          variant="light"
          rightSection={
            <Group gap={4}>
              <IconCoins stroke={1.5} size="15" /> {creditPreview}
            </Group>
          }
          disabled={
            !selectedModel ||
            (!isReferenceVideo && selectedImages.length > 1) ||
            (isReferenceVideo && !hasEnoughRefs) ||
            (needsStartingFrame && selectedImages.length === 0)
          }
          loading={isSubmitting}
          mt="sm"
          type="submit"
        >
          Create
        </Button>
      </Stack>
    </form>
  );
}
